import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuTop from "../../components/MenuTop/MenuTop";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import PaginationMaterial from "../../components/PaginationMaterial/PaginationMaterial";
import TableDocumentsReport from "../../components/TableDocumentsReport/TableDocumentsReport";
import { LoadingContext } from "../../context/LoadingContext";
import { useToast } from "../../context/ToastContext";
import { getDocumentsReportGat, getDocumentsReportExportGat, downloadDocumentGat } from "../../gateways/DocumentGateway";
import formatDateBr from "../../utils/formatDateBr";
import * as XLSX from "xlsx";
import "./DocumentsReport.css";

interface ReportRow {
  id: string;
  documentName: string;
  ownerName: string;
  ownerEmail: string;
  status: string;
  signType: string;
  createdDate: string;
  completedDate: string;
  lastActivityDate: string;
  signedSigners: number;
  totalSigners: number;
  pages: number;
  size: string;
  signerNames: string[];
  signerEmails: string[];
}

// Valores alinhados ao enum DocumentStatus do backend
const statusMap: { [key: number]: string } = {
  0: "None",
  1: "Aguardando Signatários",
  2: "Aguardando Assinaturas",
  3: "Assinado",
  4: "Finalizado",
  5: "Modificado",
  6: "Em Processamento",
  9: "Cancelado",
};

const signTypeMap: { [key: number]: string } = {
  0: "Indefinido",
  1: "Eletrônica",
  2: "Digital",
};


const formatDateOrDash = (value?: string | null) => {
  if (!value) return "-";
  return formatDateBr(value as any);
};

const formatSize = (size: number) => {
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }
  if (size >= 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  }
  return `${size} B`;
};

const DocumentsReport: React.FC = () => {
  const navigate = useNavigate();
  const { setLoading } = useContext(LoadingContext);
  const showToast = useToast();

  const [rows, setRows] = useState<ReportRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [documentName, setDocumentName] = useState("");
  const [status, setStatus] = useState<string>("");
  const [signerEmailFilter, setSignerEmailFilter] = useState("");
  const [createdFrom, setCreatedFrom] = useState<string>("");
  const [createdTo, setCreatedTo] = useState<string>("");

  const pageSize = 15;

  const loadData = async (pageNumber: number, showLoading = true) => {
    try {
      if (showLoading) setLoading(true);

      const response = await getDocumentsReportGat({
        pageNumber,
        pageSize,
        documentName: documentName || undefined,
        ownerEmail: undefined,
        signerEmail: signerEmailFilter || undefined,
        status: status ? Number(status) : undefined,
        createdFrom: createdFrom ? `${createdFrom}T00:00:00` : undefined,
        createdTo: createdTo ? `${createdTo}T23:59:59` : undefined,
      });

      const data = response.data?.data;
      const documents = data?.documents || [];

      const mappedRows: ReportRow[] = documents.map((doc: any) => ({
        id: doc.documentId,
        documentName: doc.documentName,
        ownerName: doc.ownerName || "-",
        ownerEmail: doc.ownerEmail || "-",
        status: statusMap[doc.status] || String(doc.status),
        signType: signTypeMap[doc.signType] || String(doc.signType),
        createdDate: formatDateOrDash(doc.createdDate),
        completedDate: formatDateOrDash(doc.completedDate),
        lastActivityDate: formatDateOrDash(doc.lastActivityDate),
        signedSigners: doc.signedSigners ?? 0,
        totalSigners: doc.totalSigners ?? 0,
        pages: doc.pages ?? 0,
        size: formatSize(doc.size ?? 0),
        signerNames: doc.signerNames || [],
        signerEmails: doc.signerEmails || [],
      }));

      setRows(mappedRows);
      setPage(data?.pageNumber || pageNumber);
      setTotalPages(data?.totalPages || 0);
    } catch (error: any) {
      console.error(error);
      showToast(
        "error",
        error?.response?.data?.error?.message ||
          "Erro ao carregar relatório de documentos"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = () => {
    loadData(1);
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const response = await getDocumentsReportExportGat({
        documentName: documentName || undefined,
        ownerEmail: undefined,
        signerEmail: signerEmailFilter || undefined,
        status: status ? Number(status) : undefined,
        createdFrom: createdFrom ? `${createdFrom}T00:00:00` : undefined,
        createdTo: createdTo ? `${createdTo}T23:59:59` : undefined,
      });
      const documents = response.data?.data?.documents || [];
      const exportRows = documents.map((doc: any) => ({
        ID: doc.documentId,
        Documento: doc.documentName,
        Proprietário: doc.ownerName || "-",
        "E-mail Proprietário": doc.ownerEmail || "-",
        Status: statusMap[doc.status] ?? String(doc.status),
        Tipo: signTypeMap[doc.signType] ?? String(doc.signType),
        "Criado em": formatDateOrDash(doc.createdDate),
        "Concluído em": formatDateOrDash(doc.completedDate),
        "Última atividade": formatDateOrDash(doc.lastActivityDate),
        Assinaturas: `${doc.signedSigners ?? 0}/${doc.totalSigners ?? 0}`,
        Págs: doc.pages ?? 0,
        Tamanho: formatSize(doc.size ?? 0),
        Signatários: (doc.signerEmails || []).join("; "),
      }));
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Relatório de Documentos");
      XLSX.writeFile(wb, `relatorio-documentos-${new Date().toISOString().slice(0, 10)}.xlsx`);
      showToast("success", "Exportação concluída.");
    } catch (error: any) {
      console.error(error);
      showToast("error", error?.response?.data?.error?.message || "Erro ao exportar.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    loadData(newPage, false);
  };

  const viewDocument = (documentId: string) => {
    navigate(`/admin/sign-document?documentId=${documentId}`);
  };

  const downloadDocument = async (documentId: string) => {
    try {
      setLoading(true);
      await downloadDocumentGat(documentId, true);
      showToast("success", "Download iniciado.");
    } catch (error: any) {
      showToast("error", error?.message || "Erro ao baixar o documento.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingSpinner />
      <MenuTop />

      <div
        className="mt-3 ml-3 mr-2 pt-4 pb-4 pl-4 pr-4 d-flex"
        style={{
          height: "100%",
          backgroundColor: "#fff",
          gap: "20px",
          flexDirection: "column",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
        }}
      >
        <div className="d-flex flex-column" style={{ gap: "16px" }}>
          <span className="title-page-document">Relatório de Documentos</span>

          <div className="report-filters-row">
            <div className="report-filter-field">
              <label className="report-filter-label">Nome do Documento</label>
              <input
                type="text"
                className="report-filter-input"
                placeholder="Buscar por nome"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
              />
            </div>
            <div className="report-filter-field">
              <label className="report-filter-label">E-mail do signatário</label>
              <input
                type="text"
                className="report-filter-input"
                placeholder="E-mail do signatário"
                value={signerEmailFilter}
                onChange={(e) => setSignerEmailFilter(e.target.value)}
              />
            </div>
            <div className="report-filter-field">
              <label className="report-filter-label">Status</label>
              <select
                className="report-filter-input report-filter-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">Todos</option>
                {Object.entries(statusMap)
                  .filter(([value]) => !["3", "5", "9"].includes(value)) /* sem Assinado, Modificado, Cancelado */
                  .map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </select>
            </div>
            <div className="report-filter-field">
              <label className="report-filter-label">Data inicial (criação)</label>
              <input
                type="date"
                className="report-filter-input"
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
              />
            </div>
            <div className="report-filter-field">
              <label className="report-filter-label">Data final (criação)</label>
              <input
                type="date"
                className="report-filter-input"
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
              />
            </div>
            <div className="report-filter-field report-filter-actions">
              <label className="report-filter-label report-filter-label-invisible">Ação</label>
              <div className="report-filter-buttons">
                <button type="button" className="report-filter-btn" onClick={handleFilter}>
                  Filtrar
                </button>
                <button type="button" className="report-filter-btn report-filter-btn-export" onClick={handleExport}>
                  Exportar dados
                </button>
              </div>
            </div>
          </div>
        </div>

        <hr className="my-2" style={{ color: "#DDD", width: "100%" }} />

        <div style={{ overflowX: "auto", minWidth: 0, width: "100%" }}>
          {rows.length > 0 ? (
            <TableDocumentsReport
              rows={rows}
              viewDocument={viewDocument}
              downloadDocument={downloadDocument}
            />
          ) : (
            <div className="report-empty-state">
              <span>Nenhum documento encontrado</span>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="d-flex justify-content-end pb-2 pr-2">
            <PaginationMaterial
              onChange={handleChangePage}
              count={totalPages}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default DocumentsReport;

